// @flow
import type { Element } from 'react';

import React from 'react';
import cloneDeep from 'lodash/fp/cloneDeep.js';
import { Button, ButtonGroup, Card, CardBody, Col, FormGroup, Label, Row } from 'reactstrap';
import { FieldArray } from 'redux-form';
import {
  COMPONENT_POPULATION,
  PERFORMANCE_GROUP_LOCATIONS,
  PERFORMANCE_GROUP_NAME,
  PERFORMANCE_GROUP_RESPONSE_ITEM_KEY,
  PERFORMANCE_GROUP_SERVICE_LOCATIONS,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import Field from '#components/pages/Models/Model/sections/support/forms/Field/index.js';
import { FieldFeedback, RenderInput } from '#components/support/forms/utility/index.js';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import renderLocations from './renderLocations/index.js';
import CalculatedCapacities from './CalculatedCapacities/index.jsx';
import ResponseSelectMenu from './ResponseSelectMenu/index.js';

type Props = {|
  form: string,
  autofilled: boolean,
  componentUuid: string,
  capacityUnit: string,
  fields: {
    get: Function,
    map: Function,
    push: Function,
    remove: Function,
  },
  performanceGroup: string,
  index: number,
  showCalculatedCapacity: boolean,
  showServiceLocations: boolean,
  calculatedCapacities: Array<number>,
  edpKey: string,
|};

const PerformanceGroup = (props: Props): Element<typeof Card> => {
  const {
    form,
    autofilled,
    componentUuid,
    capacityUnit,
    fields,
    performanceGroup,
    index,
    showCalculatedCapacity,
    showServiceLocations,
    calculatedCapacities,
    edpKey,
  } = props;

  const group = fields.get(index);

  return (
    <Card key={performanceGroup}>
      <CardBody>
        <Row>
          <Col>
            <ButtonGroup className="float-right">
              <Button
                id={`${performanceGroup}.deleteBtn`}
                className="pt-0 pb-0"
                color="link"
                onClick={() => fields.remove(index)}
                disabled={autofilled}
              >
                Delete
              </Button>
              <Button
                id={`${performanceGroup}.copyBtn`}
                className="pt-0 pb-0"
                color="link"
                onClick={() =>
                  fields.push({
                    ...group,
                    name: '',
                    locations: cloneDeep(group.locations),
                  })
                }
                disabled={autofilled}
              >
                Copy
              </Button>
            </ButtonGroup>
          </Col>
        </Row>
        <Row>
          <Col lg="6">
            <FormGroup>
              <Label for={`${performanceGroup}.${PERFORMANCE_GROUP_NAME}`}>Group</Label>
              <Field
                form={form}
                id={`${performanceGroup}.${PERFORMANCE_GROUP_NAME}`}
                name={`${performanceGroup}.${PERFORMANCE_GROUP_NAME}`}
                type="text"
                externalAutofillSource={COMPONENT_POPULATION}
                component={RenderInput}
              />
              <FieldFeedback name={`${performanceGroup}.${PERFORMANCE_GROUP_NAME}`} form={form} />
            </FormGroup>
          </Col>
          <Col lg="6">
            <FormGroup>
              <Label for={`${performanceGroup}.${PERFORMANCE_GROUP_RESPONSE_ITEM_KEY}`}>
                Response
              </Label>
              <ResponseSelectMenu
                form={form}
                name={`${performanceGroup}.${PERFORMANCE_GROUP_RESPONSE_ITEM_KEY}`}
                edpKey={edpKey}
                externalAutofillSource={COMPONENT_POPULATION}
              />
              <FieldFeedback
                name={`${performanceGroup}.${PERFORMANCE_GROUP_RESPONSE_ITEM_KEY}`}
                form={form}
              />
            </FormGroup>
          </Col>
        </Row>
        {showCalculatedCapacity && (
          <CalculatedCapacities
            calculatedCapacities={calculatedCapacities}
            capacityUnit={capacityUnit}
          />
        )}
        {showServiceLocations && (
          <Row>
            <Col lg="6">
              <FormGroup>
                <Label for={`${performanceGroup}.${PERFORMANCE_GROUP_SERVICE_LOCATIONS}`}>
                  Service Locations <Tip tipId={PERFORMANCE_GROUP_SERVICE_LOCATIONS} />
                </Label>
                <Field
                  form={form}
                  id={`${performanceGroup}.${PERFORMANCE_GROUP_SERVICE_LOCATIONS}`}
                  name={`${performanceGroup}.${PERFORMANCE_GROUP_SERVICE_LOCATIONS}`}
                  type="text"
                  externalAutofillSource={COMPONENT_POPULATION}
                  component={RenderInput}
                />
                <FieldFeedback
                  name={`${performanceGroup}.${PERFORMANCE_GROUP_SERVICE_LOCATIONS}`}
                  form={form}
                />
              </FormGroup>
            </Col>
          </Row>
        )}
        <FieldArray
          autofilled={autofilled}
          form={form}
          componentUuid={componentUuid}
          name={`${performanceGroup}.${PERFORMANCE_GROUP_LOCATIONS}`}
          component={renderLocations}
        />
      </CardBody>
    </Card>
  );
};

export default PerformanceGroup;
